import React, { useState } from "react";

import ArrowIcon from "../icons/ArrowIcon";
import CheckIcon from "../icons/CheckIcon";
import PropTypes from "prop-types";
import Row from "theme/layouts/Row";
import Subscribe from "core-web/libs/GrebbCommerceAPI/Newsletter/Subscribe";
import TextInput from "theme/components/inputs/TextInput";
import ThemeButton from "theme/components/buttons/ThemeButton";
import { buttonProp } from "core-web/utils/proptypes/modules/buttonsProps";
import colors from "theme/config/colors";
import styleObjectToProps from "core-web/utils/styleObjectToProps";
import styled from "theme/libs/styled";
import useFormSubmit from "core-web/hooks/useFormSubmit";
import { useTheme } from "emotion-theming";
import { useSelector } from "react-redux";

const Form = styled("form")`
    position: relative;
`;

const InputsWrapper = styled(Row)`
    border-radius: 100px;
    overflow: hidden;
`;

const Span = styled("span")`
    position: absolute;
    top: -1.6em;
`;

const NewsletterSignup = ({ email, emailInput, formStatus, isFooter, setHeading }) => {
    const colors = useTheme().colors;
    const locale = useSelector(({ application }) => application.locale);

    // Mailchimp tags; the API only accepts these three (allowed_mailchimp_tags in config/storm.php),
    // so the UK shop shares "English ROW" with the EU catalogue until a UK tag is added there.
    const tags = {
        sv_SE: "Svenska",
        en_SE: "English ROW",
        en_GB: "English ROW",
        en_US: "US/Canada",
    };

    const [state, setState] = useState({
        isLoading: false,
        isSignupComplete: false,
    });

    const { isSignupComplete, isLoading } = state;

    const onSuccessfulSubmit = async (data) => {
        setState({ isLoading: true });

        const signupData = {};

        Object.keys(data).forEach((key) => {
            const value = data[key];
            // if (key.includes("privacy-approval")) {
            //     return;
            // }

            if (key.includes("email")) {
                signupData.email = value;
            }
        });

        if (!signupData["email"]) {
            signupData["email"] = email;
        }

        if (!signupData["tags"]) {
            const tag = tags[locale];
            if (!tag) {
                throw new Error(`No tag found for locale: ${locale}`);
            }
            signupData["tags"] = [tag];
        }

        const response = await Subscribe(signupData);

        setState({ isLoading: false, isSignupComplete: true });
        setHeading(formStatus.success_message);

        const contactId = response?.data?.id;
    };

    const { error, handleSubmit, setError } = useFormSubmit({
        action: onSuccessfulSubmit,
    });

    const clearError = (e) => {
        const name = e.target.name;
        if (error.find((e) => e.name === name)) {
            const filteredError = error.filter((e) => e.name !== name);
            setError(filteredError);
        }
    };

    const hasError = !!error?.length;
    const textStyle = styleObjectToProps("Myriad Pro/18-100");

    return (
        <Form onSubmit={handleSubmit}>
            {hasError && (
                <Span {...textStyle} color={colors.red_300}>
                    {error[0]?.message}
                </Span>
            )}
            <InputsWrapper
                backgroundColor={isSignupComplete ? colors.blue_50 : colors.white}
                outline={hasError && `4px solid ${colors.red_300}`}
            >
                <TextInput
                    isRequired
                    type="email"
                    id={`newsletter-email${isFooter ? "-footer" : ""}`}
                    name="email"
                    // isRequired={emailInput.required}
                    error={error.find((e) => e.name === "email")}
                    placeholder={emailInput.placeholder || "Enter you email *"}
                    disabled={isSignupComplete}
                    handleChange={clearError}
                    customValue={isSignupComplete && (formStatus.success_message || "Subscribed!")}
                    width="100%"
                    px="16px"
                    {...textStyle}
                />

                <ThemeButton type="submit" size="xs" isLoading={isLoading} buttonTheme="transparent">
                    {isSignupComplete ? (
                        <CheckIcon height="14px" width="14px" color={colors.black} />
                    ) : (
                        <ArrowIcon direction="right" height="14px" width="14px" />
                    )}
                </ThemeButton>
            </InputsWrapper>
        </Form>
    );
};

NewsletterSignup.propTypes = {
    email: PropTypes.string,
    emailInput: PropTypes.object,
    formStatus: PropTypes.shape({
        success_message: PropTypes.string,
        error_message: PropTypes.string,
    }),
    isFooter: PropTypes.bool,
    isMemberForm: PropTypes.bool,
    privacyCheckbox: PropTypes.object,
    setHeading: PropTypes.func,
    showCategoryHeading: PropTypes.bool,
    submitButton: buttonProp,
    submitButtonStyle: PropTypes.object,
};

NewsletterSignup.defaultProps = {
    categoryButtonTheme: undefined,
    color: colors.white,
    email: "",
    emailInput: {},
    formStatus: {},
    isFooter: false,
    isMemberForm: false,
    privacyCheckbox: {},
    setHeading: () => {},
    showCategoryHeading: true,
    submitButton: {},
    submitButtonStyle: {},
};

export default NewsletterSignup;
